import React, { useRef } from "react";
import { FaBrain } from "react-icons/fa6";
import {
  FiBookOpen,
  FiCloud,
  FiFileText,
  FiImage,
  FiMusic,
  FiPhoneCall,
} from "react-icons/fi";
import { useScrollProgress } from "../components/Reveal";
import { useSteffiCopy } from "../content";

const ICONS = {
  call: FiPhoneCall,
  music: FiMusic,
  weather: FiCloud,
  news: FiFileText,
  photo: FiImage,
  recipe: FiBookOpen,
  play: FaBrain,
};

/* How far apart the queries sit, as a share of the pinned viewport. */
const SPACING = 46;
/* A query fades out exactly as its neighbour reaches the centre, so the two
   opacities always sum to 1. Anything steeper leaves the screen visibly dim
   halfway between two queries. */
const FALLOFF = 1;

export default function AsksSection() {
  const { ASKS } = useSteffiCopy();
  const railRef = useRef(null);
  const progress = useScrollProgress(railRef);

  /* the fractional index of whichever query is currently centred */
  const pos = progress * (ASKS.length - 1);

  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <section
      ref={railRef}
      id="fragen"
      className="relative"
      style={{
        height: `${ASKS.length * 55}vh`,
        background: "var(--bg)",
      }}
    >
      <div
        className="sticky top-0 flex h-[100svh] items-center justify-center overflow-hidden"
        style={{
          background:
            "var(--bg)",
        }}
      >
        <div className="relative mx-auto w-full max-w-[62rem] px-6 lg:px-10">
          {ASKS.map((ask, i) => {
            const d = i - pos;
            const away = Math.abs(d);
            const opacity = Math.max(0, 1 - away * FALLOFF);
            const Icon = ICONS[ask.icon] || FiMusic;

            return (
              <div
                key={ask.query}
                aria-hidden={opacity < 0.5}
                className="absolute inset-x-0 top-1/2 px-6 text-center lg:px-10"
                style={{
                  opacity,
                  transform:
                    `translateY(calc(-50% + ${(d * SPACING).toFixed(2)}svh))` +
                    (reduced ? "" : ` scale(${(1 - away * 0.05).toFixed(4)})`),
                  filter: reduced || away < 0.02 ? "none" : `blur(${(away * 3).toFixed(2)}px)`,
                  pointerEvents: opacity > 0.6 ? "auto" : "none",
                  willChange: "transform, opacity",
                }}
              >
                <span
                  aria-hidden
                  className="mx-auto mb-8 grid h-16 w-16 place-items-center rounded-[1.25rem] border border-[color:var(--border)] bg-white text-[color:var(--color-primary)] shadow-[0_18px_40px_-24px_rgba(36,33,30,.5)]"
                >
                  <Icon className="h-7 w-7" />
                </span>

                <p className="display text-[clamp(1.625rem,4.2vw,3.25rem)] leading-[1.1] tracking-[-0.022em]">
                  <span
                    style={{
                      background:
                        "linear-gradient(95deg, var(--accent) 0%, var(--highlight) 52%, var(--sage) 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    {ask.wake}
                  </span>{" "}
                  <span className="text-[color:var(--ink)]">{ask.query}</span>
                </p>

                <p className="mx-auto mt-5 max-w-[34rem] text-[clamp(1rem,1.4vw,1.25rem)] leading-relaxed text-[color:var(--ink-3)]">
                  {ask.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
